import type { CodeLinePayload, Hint } from "@monekineko/game-engine";
import { $, $$, Cutscene, sfx, shuffle, sleep, vibrate } from "./core.js";
import type { EngineContext, GameEngine } from "./types.js";

// Уровень «Первая строка». Перенос prototypes/2/js/code.js: та же сцена, те же
// реплики и разбор ошибки. Отличие одно и важное — правильную строку знает
// только сервер, поэтому «Запустить» спрашивает его, а не сверяется на месте.

const markup = (payload: CodeLinePayload): string => `
  <div class="screen active" data-level="code_line">
    <div class="scene" data-phase="idle">
      <div class="city-bg"></div>
      <div class="rain"></div>
      <div class="glitch-veil"></div>

      <div class="virus" hidden><img src="${payload.villainSprite}" alt=""></div>

      <div class="robot off">
        <div class="robot-glow"></div>
        <img src="${payload.characterSprite}" alt="${payload.characterName}">
      </div>

      <div class="bubble">
        <p class="bubble-text"></p>
        <span class="bubble-next"></span>
      </div>

      <div class="work">
        <div class="task"><b>Задача</b><span class="task-text"></span></div>
        <div class="editor">
          <div class="editor-bar">
            <i class="dot"></i><i class="dot"></i><i class="dot"></i>
            <span class="file">${payload.fileName}</span>
          </div>
          <div class="code"></div>
          <div class="term"></div>
        </div>
        <div class="palette"></div>
        <div class="actions">
          <button class="key-undo" data-action="undo" aria-label="Убрать последний блок"></button>
          <button class="key-run" data-action="run">Запустить код</button>
        </div>
      </div>

      <div class="alert-box" hidden>
        <div class="alert-win">
          <div class="alert-head">${payload.fileName}</div>
          <div class="alert-body"></div>
          <button class="alert-ok">ОК</button>
        </div>
      </div>

      <div class="fx"></div>
    </div>
    <div class="whiteout"></div>
  </div>`;

interface Block {
  id: string;
  text: string;
  kind: string;
}

export function createCodeLine(root: HTMLElement, ctx: EngineContext): GameEngine {
  const payload = ctx.payload as CodeLinePayload;
  root.innerHTML = markup(payload);

  const screen = $(".screen", root)!;
  const scene = $(".scene", root)!;
  const robot = $(".robot", root)!;
  const bubble = $(".bubble", root)!;
  const bubbleText = $(".bubble-text", root)!;
  const bubbleNext = $(".bubble-next", root)!;
  const taskText = $(".task-text", root)!;
  const editor = $(".editor", root)!;
  const codeEl = $(".code", root)!;
  const term = $(".term", root)!;
  const palette = $(".palette", root)!;
  const alertBox = $(".alert-box", root)!;
  const alertBody = $(".alert-body", root)!;
  const alertOk = $(".alert-ok", root)!;
  const virus = $(".virus", root)!;
  const whiteout = $(".whiteout", root)!;

  if (payload.backdrop) {
    screen.style.setProperty("--backdrop", `url(${payload.backdrop})`);
  }

  const state = {
    blocks: [] as Block[],
    placed: [] as string[],
    phase: "idle" as "idle" | "intro" | "play" | "checking" | "story" | "done",
    drag: null as null | {
      el: HTMLElement;
      id: string;
      wasPlaced: boolean;
      pointerId: number;
      dx: number;
      dy: number;
      x0: number;
      y0: number;
      width: number;
      height: number;
      moved: boolean;
      spots: { code: DOMRect; blocks: DOMRect[] } | null;
    },
  };

  const blockEls: Record<string, HTMLElement> = {};
  const caret = document.createElement("span");
  caret.className = "caret";
  let cut: Cutscene | null = null;
  let alive = true;

  const tokenOf = (id: string): string => state.blocks.find((block) => block.id === id)!.text;
  const currentCode = (): string[] => state.placed.map(tokenOf);

  function setPhase(phase: typeof state.phase): void {
    state.phase = phase;
    scene.dataset.phase = phase;
  }

  // ---------- Блоки и строка кода ----------

  function buildBlocks(): void {
    palette.replaceChildren();
    state.blocks = payload.blocks.map((block) => ({ ...block }));
    // порядок полки сервер уже перемешал; тасуем ещё раз, чтобы перезаход
    // не давал ту же раскладку
    shuffle(state.blocks);
    state.placed = [];

    state.blocks.forEach((block) => {
      const el = document.createElement("span");
      el.className = "block";
      el.dataset.id = block.id;
      el.dataset.kind = block.kind;
      el.textContent = block.text;
      blockEls[block.id] = el;
    });
  }

  /** Набранное делится на строчки после каждой точки с запятой */
  function codeLines(): string[][] {
    const lines: string[][] = [[]];
    state.placed.forEach((id) => {
      lines[lines.length - 1]!.push(id);
      if (tokenOf(id) === ";") lines.push([]);
    });
    if (lines.length > 1 && !lines[lines.length - 1]!.length) lines.pop();
    return lines;
  }

  function render(): void {
    codeEl.replaceChildren();
    codeEl.classList.toggle("empty", state.placed.length === 0);

    codeLines().forEach((ids, index) => {
      const line = document.createElement("div");
      line.className = "line";
      const num = document.createElement("i");
      num.className = "ln";
      num.textContent = String(index + 1);
      const code = document.createElement("span");
      code.className = "line-code";
      ids.forEach((id) => code.appendChild(blockEls[id]!));
      line.append(num, code);
      codeEl.appendChild(line);
    });

    $(".line:last-child .line-code", codeEl)?.appendChild(caret);

    state.blocks.forEach((block) => {
      if (!state.placed.includes(block.id)) palette.appendChild(blockEls[block.id]!);
    });
  }

  function clearMarks(): void {
    $$(".block.bad", root).forEach((el) => el.classList.remove("bad"));
  }

  function changed(): void {
    clearMarks();
    render();
    editor.classList.toggle("full", state.placed.length === state.blocks.length);
  }

  function put(id: string, index?: number): void {
    const at = Math.max(0, Math.min(index ?? state.placed.length, state.placed.length));
    state.placed.splice(at, 0, id);
    sfx.drop();
    vibrate(10);
  }

  function pull(id: string): number {
    const at = state.placed.indexOf(id);
    if (at >= 0) state.placed.splice(at, 1);
    return at;
  }

  // ---------- Перетаскивание пальцем ----------

  function onPointerDown(event: PointerEvent): void {
    if (state.phase !== "play") return;
    const el = (event.target as HTMLElement).closest<HTMLElement>(".block");
    if (!el) return;
    event.preventDefault();

    const id = el.dataset.id!;
    const rect = el.getBoundingClientRect();
    const wasPlaced = pull(id) >= 0;
    clearMarks();
    render();

    state.drag = {
      el,
      id,
      wasPlaced,
      pointerId: event.pointerId,
      dx: event.clientX - rect.left,
      dy: event.clientY - rect.top,
      x0: event.clientX,
      y0: event.clientY,
      width: rect.width,
      height: rect.height,
      moved: false,
      spots: null,
    };
    el.setPointerCapture(event.pointerId);
    sfx.pick();
  }

  /** Границы разложенных блоков снимаются один раз: каретка их потом сдвинет */
  function snapshot(): { code: DOMRect; blocks: DOMRect[] } {
    return {
      code: codeEl.getBoundingClientRect(),
      blocks: state.placed.map((id) => blockEls[id]!.getBoundingClientRect()),
    };
  }

  function indexAt(spots: { code: DOMRect; blocks: DOMRect[] }, x: number, y: number): number | null {
    const r = spots.code;
    if (x < r.left || x > r.right || y < r.top || y > r.bottom) return null;
    for (let i = 0; i < spots.blocks.length; i++) {
      const b = spots.blocks[i]!;
      if (y < b.bottom && x < b.left + b.width / 2) return i;
    }
    return spots.blocks.length;
  }

  function showCaretAt(index: number | null): void {
    if (index === null || index >= state.placed.length) {
      $(".line:last-child .line-code", codeEl)?.appendChild(caret);
    } else {
      const el = blockEls[state.placed[index]!]!;
      el.parentElement?.insertBefore(caret, el);
    }
    caret.classList.toggle("drop", index !== null);
  }

  function onPointerMove(event: PointerEvent): void {
    const drag = state.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (!drag.moved && Math.hypot(event.clientX - drag.x0, event.clientY - drag.y0) < 6) return;

    if (!drag.moved) {
      drag.moved = true;
      drag.spots = snapshot();
      drag.el.style.width = `${drag.width}px`;
      drag.el.style.height = `${drag.height}px`;
      drag.el.classList.add("dragging");
    }
    drag.el.style.left = `${event.clientX - drag.dx}px`;
    drag.el.style.top = `${event.clientY - drag.dy}px`;
    showCaretAt(indexAt(drag.spots!, event.clientX, event.clientY));
  }

  function onPointerUp(event: PointerEvent): void {
    const drag = state.drag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    state.drag = null;
    caret.classList.remove("drop");

    if (!drag.moved) {
      // короткое касание: с полки блок встаёт в конец строки, из строки уходит на полку
      if (drag.wasPlaced) sfx.undo();
      else put(drag.id, state.placed.length);
      changed();
      return;
    }

    const index = indexAt(drag.spots!, event.clientX, event.clientY);
    drag.el.classList.remove("dragging");
    drag.el.style.left = "";
    drag.el.style.top = "";
    drag.el.style.width = "";
    drag.el.style.height = "";
    if (index !== null) put(drag.id, index);
    else if (drag.wasPlaced) sfx.undo();
    changed();
  }

  scene.addEventListener("pointerdown", onPointerDown);
  scene.addEventListener("pointermove", onPointerMove);
  scene.addEventListener("pointerup", onPointerUp);
  scene.addEventListener("pointercancel", onPointerUp);

  // ---------- Запуск ----------

  function print(text: string, cls: string): void {
    const line = document.createElement("div");
    line.className = `out ${cls}`;
    line.textContent = text;
    term.appendChild(line);
    term.scrollTop = term.scrollHeight;
  }

  function say(text: string): void {
    bubbleText.textContent = text;
    bubble.classList.add("show", "pop");
    setTimeout(() => bubble.classList.remove("pop"), 400);
  }

  async function run(): Promise<void> {
    if (state.phase !== "play") return;
    const code = currentCode();

    setPhase("checking");
    clearMarks();
    term.replaceChildren();
    sfx.run();
    print(`> ${code.join(" ") || "—"}`, "echo");
    ctx.event("run", { length: code.length });
    await sleep(320);
    if (!alive) return;

    // Правильную строку знает сервер: он же объясняет, что не так
    const verdict = await ctx.check({ code });
    if (!alive) return;

    if (verdict.solved) {
      await solved(code);
      return;
    }

    const details = (verdict.details ?? {}) as { error?: string; hint?: string; at?: number | null };
    print(`✕ ${details.error ?? "Код не выполнился"}`, "err");
    if (details.hint) print(details.hint, "hint");

    if (details.at != null) {
      const id = state.placed[details.at];
      if (id) blockEls[id]?.classList.add("bad");
    }

    editor.classList.add("shake");
    setTimeout(() => editor.classList.remove("shake"), 420);
    sfx.error();
    vibrate([40, 60, 40]);
    say(payload.errorReply);
    setPhase("play");
  }

  function undo(): void {
    if (state.phase !== "play" || !state.placed.length) {
      sfx.deny();
      return;
    }
    state.placed.pop();
    sfx.undo();
    vibrate(8);
    ctx.event("undo");
    changed();
  }

  scene.addEventListener("click", (event) => {
    const action = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!action) return;
    if (action.dataset.action === "run") void run();
    if (action.dataset.action === "undo") undo();
  });

  // ---------- Верный ответ ----------

  async function solved(code: string[]): Promise<void> {
    setPhase("done");
    bubble.classList.remove("show");
    editor.classList.add("ok");
    print("✓ Код выполнен", "ok");
    sfx.success();
    vibrate([30, 60, 30]);

    // Зачёт уходит сразу: история впереди длинная, и её время игроку не в счёт
    const verdict = await ctx.submit({ code });
    if (!alive) return;

    await sleep(600);
    if (!alive) return;

    alertBody.textContent = payload.answerText;
    alertBox.hidden = false;
    void alertBox.offsetWidth;
    alertBox.classList.add("show");
    sfx.pop();

    alertOk.addEventListener(
      "click",
      () => {
        alertBox.classList.remove("show");
        sfx.pick();
        setTimeout(() => {
          alertBox.hidden = true;
        }, 260);
        void playStory(verdict);
      },
      { once: true },
    );
  }

  // ---------- Реплики ----------

  async function speak(c: Cutscene, text: string): Promise<void> {
    bubble.classList.add("show");
    bubbleNext.classList.remove("show");
    bubbleText.textContent = "";

    let rushed = false;
    const rush = (): void => {
      rushed = true;
    };
    scene.addEventListener("pointerdown", rush);

    try {
      for (let i = 0; i < text.length; i++) {
        bubbleText.textContent = text.slice(0, i + 1);
        if (i % 2 === 0) sfx.type();
        if (rushed) {
          bubbleText.textContent = text;
          break;
        }
        await c.later(26);
      }
    } finally {
      scene.removeEventListener("pointerdown", rush);
    }

    bubbleNext.classList.add("show");
    const hold = c.hold();
    const next = (): void => hold.done();
    scene.addEventListener("pointerdown", next, { once: true });
    try {
      await hold.promise;
    } finally {
      scene.removeEventListener("pointerdown", next);
      bubbleNext.classList.remove("show");
    }
  }

  /** Вспышка воспоминания: по улице проносится тот самый вирус */
  async function virusDash(c: Cutscene): Promise<void> {
    scene.classList.add("glitching");
    sfx.corrupt();
    sfx.dash();
    virus.hidden = false;
    c.track(
      virus.animate([{ transform: "translate(-170%, 6%)" }, { transform: "translate(170%, -2%)" }], {
        duration: 950,
        easing: "cubic-bezier(.35,0,.65,1)",
      }),
    );
    await c.later(950);
    virus.hidden = true;
    scene.classList.remove("glitching");
  }

  function setWhite(on: boolean, animated: boolean): void {
    if (!animated) whiteout.style.transition = "none";
    whiteout.classList.toggle("show", on);
    if (!animated) {
      void whiteout.offsetWidth;
      whiteout.style.transition = "";
    }
  }

  // ---------- Заставка ----------

  function prepare(): void {
    buildBlocks();
    taskText.textContent = payload.task;
    term.replaceChildren();
    editor.classList.remove("ok", "full", "shake");
    alertBox.classList.remove("show");
    alertBox.hidden = true;
    virus.hidden = true;
    scene.classList.remove("glitching");
    robot.classList.add("off");
    bubble.classList.remove("show");
    bubbleText.textContent = "";
    render();
  }

  async function playIntro(): Promise<void> {
    cut = new Cutscene();
    const c = cut;
    setPhase("intro");
    setWhite(true, false);

    try {
      // Предыдущий уровень кончился белым экраном — из него и выходим
      await c.later(60);
      setWhite(false, true);
      sfx.city();
      await c.later(1600);

      // Робот лежит тёмный, потом внутри него что-то щёлкает
      sfx.boot();
      robot.classList.add("booting");
      await c.later(800);
      robot.classList.remove("off", "booting");
      sfx.servo();
      c.track(
        robot.animate(
          [
            { transform: "translate(-50%, -50%) translateY(5%) rotate(-9deg)" },
            { transform: "translate(-50%, -50%) translateY(-2%) rotate(3deg)", offset: 0.6 },
            { transform: "translate(-50%, -50%) rotate(0deg)" },
          ],
          { duration: 1200, easing: "cubic-bezier(.3,1.4,.5,1)" },
        ),
      );
      await c.later(1000);

      for (const line of payload.introLines) await speak(c, line);
    } catch {
      return;
    }
    finishIntro();
  }

  function stopCutscene(): void {
    if (!cut) return;
    cut.stop();
    cut = null;
  }

  function showLevel(): void {
    setWhite(false, false);
    robot.classList.remove("off", "booting");
    virus.hidden = true;
    scene.classList.remove("glitching");
    bubbleNext.classList.remove("show");
    say(payload.waitReply);
    setPhase("play");
    changed();
  }

  function finishIntro(): void {
    stopCutscene();
    showLevel();
    ctx.introDone();
  }

  // ---------- История робота ----------

  async function playStory(verdict: Awaited<ReturnType<EngineContext["submit"]>>): Promise<void> {
    cut = new Cutscene();
    const c = cut;
    setPhase("story");

    try {
      await c.later(500);
      for (const line of payload.storyLines) {
        if (line.virus) await virusDash(c);
        await speak(c, line.text);
      }
      bubble.classList.remove("show");
      await c.later(600);
      sfx.whoosh();
      setWhite(true, true);
      await c.later(1200);
    } catch {
      return;
    }
    finishStory(verdict);
  }

  function finishStory(verdict: Awaited<ReturnType<EngineContext["submit"]>>): void {
    stopCutscene();
    virus.hidden = true;
    scene.classList.remove("glitching");
    bubble.classList.remove("show");
    setWhite(true, false);
    setPhase("done");
    ctx.finished(verdict);
  }

  prepare();
  if (ctx.playIntro) void playIntro();
  else finishIntro();

  return {
    destroy() {
      alive = false;
      stopCutscene();
      root.innerHTML = "";
    },
    skipIntro: finishIntro,
    canHint: () => state.phase === "play",
    applyHint(hint: Hint) {
      const data = hint.data as { index: number; token: string };
      print(`💡 ${hint.text}`, "hint");
      // подсвечиваем нужный блок на полке, чтобы его было легко найти
      const block = state.blocks.find(
        (item) => item.text === data.token && !state.placed.includes(item.id),
      );
      if (!block) return;
      const el = blockEls[block.id];
      el?.animate(
        [
          { boxShadow: "0 0 0 1px #46586b" },
          { boxShadow: "0 0 18px 4px rgba(62,240,255,0.85)" },
          { boxShadow: "0 0 0 1px #46586b" },
        ],
        { duration: 800, iterations: 3 },
      );
    },
  };
}
